import React, { useState, useEffect } from 'react';
import {
  FaCalculator, FaBrain, FaFlag, FaCodeBranch, FaCoins, FaDiagnoses,
  FaGlobe, FaDollarSign, FaChartLine, FaHandHoldingHeart, FaHandsHelping,
  FaUsers, FaClipboardCheck, FaLaptop, FaProjectDiagram, FaUserFriends,
  FaBuilding, FaBoxes, FaHandshake, FaNetworkWired, FaUserTie, FaBalanceScale,
  FaPlaneDeparture, FaMoneyBillWave, FaSeedling, FaChartPie, FaWarehouse,
  FaStore, FaReceipt, FaArrowRight, FaArrowLeft
  // Removed FaInfoCircle as it was only used in the deleted YesNoToggle
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import './ComplexityFactors.css'; // Assume this CSS file exists and styles appropriately

// Removed the YesNoToggle helper component as it's no longer needed

// Main Component
const ComplexityFactors = ({ factors, onChange, onCalculate }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isCalculating, setIsCalculating] = useState(false);
  const totalSteps = 9; // Adjusted total steps

  // Ensure default values for financial flags and initialize the array holder
  useEffect(() => {
    const defaultFactors = {
      financialComplexityFlags: factors.financialComplexityFlags || [], // Initialize the array holder
      hasUnrelatedIncome: factors.hasUnrelatedIncome ?? false,
      hasForeignAccounts: factors.hasForeignAccounts ?? false,
      hasComplexCompensation: factors.hasComplexCompensation ?? false,
      hasInvestments: factors.hasInvestments ?? false,
    };
    // Only update if any defaults were missing
    if (Object.keys(defaultFactors).some(key => factors[key] === undefined)) {
      onChange({ ...factors, ...defaultFactors });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Define steps data - Step 7 RESTRUCTURED
  const steps = [
    // Step 1: Mission & Subsector
    {
      number: 1,
      title: "Mission & Focus Area",
      subtitle: "What's the primary NTEE category for your work?",
      field: 'subsector',
      options: [
        { value: 'arts', label: 'Arts, Culture & Humanities', description: 'Museums, performing arts, media' },
        { value: 'education', label: 'Education', description: 'Schools, universities, literacy programs' },
        { value: 'environment', label: 'Environment & Animals', description: 'Conservation, zoos, animal welfare' },
        { value: 'health', label: 'Health', description: 'Hospitals, clinics, mental health, research' },
        { value: 'human_services', label: 'Human Services', description: 'Housing, food banks, youth development' },
        { value: 'international', label: 'International Affairs', description: 'Relief, development, human rights abroad' },
        { value: 'public_societal_benefit', label: 'Public, Societal Benefit', description: 'Advocacy, civil rights, community improvement' },
        { value: 'religion', label: 'Religion Related', description: 'Churches, synagogues, ministries (often have exceptions)' },
        { value: 'other', label: 'Other/Not Sure', description: 'If none fit well' }
      ]
    },
    // Step 2: Reach
    {
      number: 2,
      title: "Geographic Reach",
      subtitle: "Where will your organization primarily operate?",
      field: 'geographicScope',
      options: [
        { value: 'local', label: 'Local', description: 'Single city or county' },
        { value: 'regional', label: 'Regional', description: 'Multiple counties' },
        { value: 'state', label: 'State-wide', description: 'Entire state coverage' },
        { value: 'multi_state', label: 'Multi-State', description: 'Operating in multiple states' },
        { value: 'national', label: 'National', description: 'Nationwide operations' }
      ]
    },
    // Step 3: Programs
    {
      number: 3,
      title: "Program Structure",
      subtitle: "How many distinct programs will you operate?",
      field: 'programCount', // Keep this field name for consistency
      options: [
        { value: 1, label: '1 program', description: 'Single program focus' },
        { value: 2, label: '2-3 programs', description: 'Multiple core programs' },
        { value: 4, label: '4-5 programs', description: 'Diverse program portfolio' },
        { value: 6, label: '6+ programs', description: 'Complex multi-program organization' }
      ]
    },
    // Step 4: Team
    {
      number: 4,
      title: "Team Structure & Size",
      subtitle: "Describe your team and estimate paid employee count.",
      fields: [ // Multiple fields in one step
        {
          name: 'staffingModel',
          label: 'Primary Staffing Model:',
          type: 'select',
          options: [
            { value: 'volunteer', label: 'Volunteer-Driven', description: 'Primarily volunteer-operated' },
            { value: 'hybrid', label: 'Hybrid', description: 'Mix of paid staff and volunteers' },
            { value: 'professional', label: 'Professional Staff', description: 'Primarily paid employees' },
            { value: 'contractors', label: 'Contractor-Based', description: 'Mainly independent contractors' },
            { value: 'minimal', label: 'Minimal Staff', description: 'Small core team only' }
          ]
        },
        {
          name: 'employeeCount',
          label: 'Estimated Paid Employees (FTEs):',
          type: 'number',
          placeholder: 'Enter number (e.g., 0, 5, 15)',
          min: 0,
          max: 1000 // Reasonable upper limit
        }
      ]
    },
    // Step 5: Assets & Inventory
    {
      number: 5,
      title: "Assets & Inventory",
      subtitle: "What types of physical assets or inventory will you manage?",
      field: 'inventoryType', // Maps to hasInventory boolean later
      multiSelect: true,
      options: [
        { value: 'none', label: 'No Significant Inventory', description: 'No major physical assets to track' },
        { value: 'supplies', label: 'Program Supplies', description: 'Consumable materials for programs' },
        { value: 'merchandise', label: 'Merchandise for Sale', description: 'Items sold to generate revenue' },
        { value: 'equipment', label: 'Significant Equipment', description: 'Fixed assets like vehicles, tech hardware' },
        { value: 'facilities', label: 'Real Estate / Facilities', description: 'Own or manage buildings/land' }
      ]
    },
    // Step 6: Funding Sources
    {
      number: 6,
      title: "Primary Funding Sources",
      subtitle: "How will your organization be primarily funded? (Select all major types)",
      field: 'fundingSources',
      multiSelect: true,
      options: [
        { value: 'individual', label: 'Individual Donations', description: 'Contributions from general public' },
        { value: 'foundation', label: 'Foundation Grants', description: 'Funding from private/family foundations' },
        { value: 'government', label: 'Government Grants/Contracts', description: 'Federal, state, or local funding' },
        { value: 'earned', label: 'Earned Revenue', description: 'Fees for services, program income, sales' },
        { value: 'corporate', label: 'Corporate Sponsorships/Grants', description: 'Funding from businesses' },
        { value: 'events', label: 'Special Events', description: 'Fundraising events and campaigns' }
      ]
    },
    // Step 7: Financial Details - CHANGED to Multi-Select Card UI
    {
      number: 7,
      title: "Financial Details & Complexity",
      subtitle: "Select all financial complexities that apply to your organization.", // Updated subtitle
      field: 'financialComplexityFlags', // New field to hold the array of selected flags
      multiSelect: true, // Indicate this uses the multi-select card UI
      options: [ // Transform original toggle fields into options
        {
          value: 'hasUnrelatedIncome', // Value corresponds to the original boolean flag name
          label: 'Generate Unrelated Business Income (UBIT)?', // Original label
          description: 'Income from activities not substantially related to your tax-exempt purpose (e.g., regular sales of merchandise, advertising).' // Original tooltip
        },
        {
          value: 'hasForeignAccounts',
          label: 'Maintain Foreign Bank Accounts or Operations?',
          description: 'Holding bank accounts outside the US or conducting program activities internationally.'
        },
        {
          value: 'hasComplexCompensation',
          label: 'Use Complex Compensation Structures?',
          description: 'E.g., multi-part salaries, deferred compensation, significant non-cash benefits for executives.'
        },
        {
          value: 'hasInvestments',
          label: 'Hold Significant Investments?',
          description: 'Managing endowments, stocks, bonds, or alternative investments beyond basic operating reserves.'
        }
      ]
      // Removed 'type: toggles' and 'fields' array
    },
    // Step 8: Vendors
    {
      number: 8,
      title: "Vendor Management",
      subtitle: "Estimate your monthly vendor volume and complexity.",
      fields: [
        {
          name: 'vendorTransactions',
          label: 'Expected Monthly Vendor Payments:',
          type: 'select',
          options: [
            { value: '1-10', label: '1-10 monthly', description: 'Low transaction volume' },
            { value: '11-50', label: '11-50 monthly', description: 'Moderate transaction volume' },
            { value: '51-200', label: '51-200 monthly', description: 'High transaction volume' },
            { value: '201+', label: '201+ monthly', description: 'Very high transaction volume' }
          ]
        },
        {
          name: 'vendorComplexity',
          label: 'Types of Vendors (Select all that apply):',
          type: 'multi-select',
          options: [
            { value: 'simple', label: 'Basic Services (Utilities, Rent)', description: 'Standard operational vendors' },
            { value: 'program', label: 'Program Partners/Subgrantees', description: 'Partners involved in service delivery' },
            { value: 'professional', label: 'Professional Services (Legal, CPA)', description: 'Specialized consultants/firms' },
            { value: 'international', label: 'International Vendors', description: 'Vendors based outside the US' },
            { value: 'government', label: 'Government Agencies', description: 'Interactions as vendors or contractors' }
          ]
        }
      ]
    },
    // Step 9: Growth Stage
    {
      number: 9,
      title: "Organizational Growth Stage",
      subtitle: "Select the phase that best describes your organization.",
      field: 'growthStage',
      options: [
        { value: 'startup', label: 'Startup / Idea Phase', description: 'Just starting, defining programs' },
        { value: 'growing', label: 'Early Growth (1-3 yrs)', description: 'Establishing operations, growing programs' },
        { value: 'established', label: 'Established (3-10 yrs)', description: 'Stable operations, refining processes' },
        { value: 'scaling', label: 'Scaling / Expansion', description: 'Rapid growth, adding locations/programs' },
        { value: 'mature', label: 'Mature (10+ yrs)', description: 'Well-established, diversified programs' }
      ]
    },
  ];

  // Get current step data
  const currentStepData = steps[currentStep - 1];

  const handleInputChange = (field, value) => {
    let updatedFactors = { ...factors };

    // Specific handling for inventoryType to map to hasInventory (existing)
    if (field === 'inventoryType') {
      const hasInventory = Array.isArray(value) && value.length > 0 && !value.includes('none');
      updatedFactors = {
        ...updatedFactors,
        [field]: value,
        hasInventory: hasInventory
      };
    }
    // *** NEW HANDLING for financialComplexityFlags ***
    // Store the selected array AND derive the individual boolean flags
    else if (field === 'financialComplexityFlags') {
      const selectedFlags = Array.isArray(value) ? value : [];
      updatedFactors = {
        ...updatedFactors,
        [field]: selectedFlags, // Store the array itself
        // Derive boolean flags from the array for downstream compatibility
        hasUnrelatedIncome: selectedFlags.includes('hasUnrelatedIncome'),
        hasForeignAccounts: selectedFlags.includes('hasForeignAccounts'),
        hasComplexCompensation: selectedFlags.includes('hasComplexCompensation'),
        hasInvestments: selectedFlags.includes('hasInvestments'),
      };
    }
    // Standard handling for other fields
    else {
      updatedFactors = {
        ...updatedFactors,
        [field]: value
      };
    }
    onChange(updatedFactors); // Call onChange once with all updates
  };


  const handleMultiFieldChange = (fieldName, fieldValue) => {
    // Special handling for inventoryType in multi-field steps if needed later
    if (fieldName === 'inventoryType') {
      const hasInventory = Array.isArray(fieldValue) && fieldValue.length > 0 && !fieldValue.includes('none');
      onChange({ ...factors, [fieldName]: fieldValue, hasInventory: hasInventory });
    } else {
      onChange({ ...factors, [fieldName]: fieldValue });
    }
  };

  // Render Progress Bar - Icons and Labels (Unchanged)
  const progressSteps = [
    { number: 1, icon: <FaFlag />, label: "Mission" },
    { number: 2, icon: <FaGlobe />, label: "Reach" },
    { number: 3, icon: <FaProjectDiagram />, label: "Programs" },
    { number: 4, icon: <FaUsers />, label: "Team" },
    { number: 5, icon: <FaWarehouse />, label: "Assets" },
    { number: 6, icon: <FaHandHoldingHeart />, label: "Funding" },
    { number: 7, icon: <FaBalanceScale />, label: "Finance" }, // Icon remains the same
    { number: 8, icon: <FaHandshake />, label: "Vendors" },
    { number: 9, icon: <FaChartLine />, label: "Growth" },
  ];

  const renderProgress = () => (
    <div className="wizard-progress">
      {progressSteps.map((step, index) => (
        <div key={index} className="progress-step-container">
          <div className={`progress-step ${index + 1 <= currentStep ? 'active' : ''}`}>
            {step.icon}
            {/* Optional: Keep label for larger screens if desired */}
            {/* <span className="step-label">{step.label}</span> */}
          </div>
          {index < progressSteps.length - 1 && (
            <div className={`progress-line ${index + 1 < currentStep ? 'active' : ''}`} />
          )}
        </div>
      ))}
    </div>
  );

  const handleCalculate = async () => {
    setIsCalculating(true);
    try {
      // Simulate calculation time
      await new Promise(resolve => setTimeout(resolve, 1500));
      // Map programCount value if needed (e.g., if UI sends '6+', map to 6)
      let finalFactors = { ...factors };
      if (typeof finalFactors.programCount === 'string') {
        // Basic mapping, adjust if more complex logic needed
        if (finalFactors.programCount.includes('+')) {
          finalFactors.programCount = parseInt(finalFactors.programCount.replace('+', ''), 10);
        } else if (finalFactors.programCount.includes('-')) {
          // Use the lower bound or average? Let's use lower bound for simplicity
          finalFactors.programCount = parseInt(finalFactors.programCount.split('-')[0], 10);
        }
      }
      // Ensure employeeCount is a number
      finalFactors.employeeCount = parseInt(finalFactors.employeeCount, 10) || 0;

      // Ensure hasInventory is correctly set based on inventoryType
      finalFactors.hasInventory = Array.isArray(finalFactors.inventoryType) && finalFactors.inventoryType.length > 0 && !finalFactors.inventoryType.includes('none');

      // The boolean financial flags (hasUnrelatedIncome, etc.) should already be correctly set
      // in the factors state by the updated handleInputChange function.
      // We can optionally remove the temporary financialComplexityFlags array before sending.
      delete finalFactors.financialComplexityFlags;


      console.log("Factors sent to calculation:", finalFactors); // Debugging
      onCalculate(finalFactors);
    } catch (error) {
      console.error("Calculation error:", error);
      // Handle error state if necessary
    } finally {
      setIsCalculating(false);
    }
  };

  const AIProcessingAnimation = () => (
    <div className="ai-processing-container">
      <div className="neural-processor">
        <div className="brain-core">
          <FaBrain className="brain-icon" />
          <div className="neural-rings">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`ring ring-${i + 1}`} />
            ))}
          </div>
          <div className="neural-particles">
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`particle particle-${i + 1}`} />
            ))}
          </div>
        </div>
        <div className="processing-status">
          <span className="status-text">Analyzing Costs</span>
          <span className="dots">...</span>
        </div>
      </div>
      <div className="progress-track">
        <div className="progress-pulse" />
      </div>
    </div>
  );

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderNavigation = () => (
    <div className="wizard-navigation">
      {currentStep > 1 && (
        <button
          className="nav-button back"
          onClick={handleBack}
        >
          <FaArrowLeft /> Back
        </button>
      )}
      {currentStep < totalSteps ? (
        <button
          className="nav-button next"
          onClick={handleNext}
          // Basic validation: disable next if required field is empty (needs refinement)
          disabled={currentStepData.field && !factors[currentStepData.field] && !currentStepData.multiSelect}
        >
          Next <FaArrowRight />
        </button>
      ) : (
        <button
          className="analyze-button"
          onClick={handleCalculate}
          disabled={isCalculating}
        >
          {isCalculating ? (
            <AIProcessingAnimation />
          ) : (
            <>
              <FaCalculator className="button-icon" />
              <span>Analyze Cost Dynamics</span>
            </>
          )}
        </button>
      )}
    </div>
  );

  // RENDER LOGIC FOR DIFFERENT STEP TYPES
  const renderStepContent = () => {
    // Case 1: Standard single-select options
    if (currentStepData.options && !currentStepData.fields && !currentStepData.multiSelect) {
      return (
        <div className="options-grid">
          {currentStepData.options.map((option) => (
            <button
              key={option.value}
              className={`option-card ${factors[currentStepData.field] === option.value ? 'selected' : ''}`}
              onClick={() => {
                handleInputChange(currentStepData.field, option.value);
                // Auto-advance on single select
                if (currentStep < totalSteps) {
                  // Small delay for visual feedback
                  setTimeout(() => setCurrentStep(currentStep + 1), 150);
                }
              }}
            >
              <div className="option-content">
                <h3>{option.label}</h3>
                {option.description && <p>{option.description}</p>}
              </div>
              <div className="option-indicator" />
            </button>
          ))}
        </div>
      );
    }

    // Case 2: Standard multi-select options (NOW HANDLES STEP 7 AS WELL)
    if (currentStepData.options && currentStepData.multiSelect) {
       // Ensure we have an array, even if the field hasn't been initialized yet
       const currentSelection = Array.isArray(factors[currentStepData.field]) ? factors[currentStepData.field] : [];
      return (
        <div className="options-grid multi-select">
          {currentStepData.options.map((option) => (
            <button
              key={option.value}
              className={`option-card ${currentSelection.includes(option.value) ? 'selected' : ''}`}
              onClick={() => {
                let newValue;
                // Handle 'none' exclusivity only if 'none' is an option for this step
                const hasNoneOption = currentStepData.options.some(o => o.value === 'none');

                if (hasNoneOption && option.value === 'none') {
                   // If 'none' is clicked, select only 'none'
                  newValue = currentSelection.includes('none') ? [] : ['none'];
                } else {
                  // Remove 'none' if another option is selected
                  const filteredSelection = currentSelection.filter(v => v !== 'none');
                  if (filteredSelection.includes(option.value)) {
                    // Deselect
                    newValue = filteredSelection.filter(v => v !== option.value);
                  } else {
                    // Select
                    newValue = [...filteredSelection, option.value];
                  }
                  // If becomes empty and 'none' exists, maybe default to 'none'? Let's default to empty.
                  // if (newValue.length === 0 && hasNoneOption) {
                  //   newValue = ['none'];
                  // }
                }
                handleInputChange(currentStepData.field, newValue);
              }}
            >
              <div className="option-content">
                <h3>{option.label}</h3>
                {option.description && <p>{option.description}</p>}
              </div>
              <div className={`option-indicator multi ${currentSelection.includes(option.value) ? 'selected' : ''}`} />
            </button>
          ))}
        </div>
      );
    }


    // Case 3: Multi-field step (like Team or Vendors)
    if (currentStepData.fields && !currentStepData.options) {
        // Special handling for Vendor Step (8) to arrange in columns
        if (currentStepData.number === 8) {
            return (
                <div className="vendor-columns">
                    {currentStepData.fields.map((field, index) => (
                        <div key={field.name} className="vendor-column">
                             <h4 className="column-header">{field.label}</h4>
                            {field.type === 'select' && (
                               <div className="options-list">
                                    {field.options.map(option => (
                                        <button
                                            key={option.value}
                                            className={`option-card ${factors[field.name] === option.value ? 'selected' : ''}`}
                                            onClick={() => handleMultiFieldChange(field.name, option.value)}
                                        >
                                            <div className="option-content">
                                                <h3>{option.label}</h3>
                                                {option.description && <p>{option.description}</p>}
                                            </div>
                                            <div className="option-indicator" />
                                        </button>
                                    ))}
                                </div>
                            )}
                             {field.type === 'multi-select' && (
                               <div className="options-list">
                                    {field.options.map(option => {
                                        const currentSelection = factors[field.name] || [];
                                        return (
                                            <button
                                                key={option.value}
                                                className={`option-card ${currentSelection.includes(option.value) ? 'selected' : ''}`}
                                                onClick={() => {
                                                    const newValue = currentSelection.includes(option.value)
                                                        ? currentSelection.filter(v => v !== option.value)
                                                        : [...currentSelection, option.value];
                                                    handleMultiFieldChange(field.name, newValue);
                                                }}
                                            >
                                                <div className="option-content">
                                                    <h3>{option.label}</h3>
                                                    {option.description && <p>{option.description}</p>}
                                                </div>
                                                <div className={`option-indicator multi ${currentSelection.includes(option.value) ? 'selected' : ''}`} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }
        // Default rendering for other multi-field steps (like Team Step 4)
        return (
             <div className="team-grid">
                 {currentStepData.fields.map(field => (
                     <div key={field.name} className="team-section">
                         <label htmlFor={field.name} className="field-label">{field.label}</label>
                        {field.type === 'select' && (
                             <div className="options-list">
                                 {field.options.map(option => (
                                     <button
                                         key={option.value}
                                         className={`option-card compact ${factors[field.name] === option.value ? 'selected' : ''}`}
                                         onClick={() => handleMultiFieldChange(field.name, option.value)}
                                     >
                                         <div className="option-content">
                                             <h3>{option.label}</h3>
                                             {option.description && <p>{option.description}</p>}
                                         </div>
                                         <div className="option-indicator" />
                                     </button>
                                 ))}
                             </div>
                         )}
                        {field.type === 'number' && (
                           <div className="input-container">
                                <input
                                    id={field.name}
                                    type="number"
                                    className="employee-input"
                                    value={factors[field.name] || ''}
                                    onChange={(e) => handleMultiFieldChange(field.name, e.target.value ? parseInt(e.target.value, 10) : '')}
                                    placeholder={field.placeholder}
                                    min={field.min}
                                    max={field.max}
                                />
                            </div>
                         )}
                         {/* Add handling for other field types within multi-field steps if needed */}
                     </div>
                 ))}
             </div>
        );
    }

    // Case 4: Yes/No Toggles step - THIS BLOCK IS NOW REMOVED as Step 7 uses multi-select
    // if (currentStepData.type === 'toggles') { ... } // No longer needed for Step 7


    // Fallback for unexpected structure
    return <div>Step content configuration error.</div>;
  };


  return (
    <div className="complexity-wizard">
      {renderProgress()}
      {/* Mobile Progress Counter (conditionally rendered via CSS) */}
       <div className="mobile-progress">
         <span className="progress-counter">Step {currentStep} of {totalSteps}</span>
         <span className="progress-label">{progressSteps[currentStep - 1]?.label || ''}</span>
       </div>

      <motion.div
        key={currentStep} // Animate when step changes
        className="wizard-content"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -50 }}
        transition={{ duration: 0.3 }}
      >
        <div className="step-header">
          <h2>{currentStepData.title}</h2>
          <p>{currentStepData.subtitle}</p>
        </div>

        {renderStepContent()}
        {renderNavigation()}

      </motion.div>

    </div>
  );
};

export default ComplexityFactors;